import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ShippedSummary } from "src/app/models/accounting/shipped-summary-data";
import { DashboardHeaderButton } from "src/app/models/dashboard/dashboard-header-button";
import { TableInfo } from "src/app/modules/controls/components/export-dialog/export-dialog.component";
import { DataService } from "src/app/services/data.service";
import { ErrorMessageSet, OpenSearch, SetDashboardHeaderButtons, SetDashboardTitle, ShowDownloadDialog } from "src/app/store/actions/site.actions";
import { IAppState } from "src/app/store/state/app.state";
import { Permissions, PermissionMetadata } from 'src/app/modules/controls/enums/permissions';
import { AuthService } from "src/app/services/auth.service";
import * as XLSX from 'xlsx';
import { ViewChild, ElementRef } from '@angular/core';
import { Table } from 'primeng/table';
import { DomSanitizer, SafeUrl, SafeHtml } from '@angular/platform-browser';


@Component({
  selector: "app-invoices",
  templateUrl: "./invoices.component.html",
  styleUrls: ["./invoices.component.scss"]
})




export class InvoicesComponent implements OnInit {

  public  tabNameMap: { [key: string]: string } = {
    MSS: 'Open Invoices',
    MSH: 'Paid Invoices',
    BOS: 'Back Order Summary',
    OSS: 'Orders Shipped Summary'
  };


  @ViewChild('globalFilter') globalFilter!: ElementRef;

  public openTab: string = "MSS";
  public allData: { [key: string]: Array<any> } = {};
  public tableInfo: TableInfo;
  public pageSize: number = 300;
  public pageNumber: number = 1;
  public pageCount: number;
  public searchstring: string = null;
  public Permissions = Permissions;
  public loading: boolean = false;
  public visibleData: { [key: string]: any[] } = {};
  public totalRecords: { [key: string]: number } = {};

 


  constructor(private store: Store<IAppState>, private dataService: DataService, private route: ActivatedRoute, private authService: AuthService, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.searchstring = this.route.snapshot.queryParamMap.get('searchCriteria');
    this.store.dispatch(new SetDashboardTitle("Member Statement Summary"));

    const buttons: DashboardHeaderButton[] = [
      new DashboardHeaderButton({ text: "Search", cssClass: "search-button", icon: "search", callback: () => this.store.dispatch(new OpenSearch(1)) }),
      new DashboardHeaderButton({ text: "Download", icon: "download", callback: () => this.exportAllTabsToExcel() })
    ];
    this.store.dispatch(new SetDashboardHeaderButtons(buttons));

    this.route.paramMap.subscribe(t => {
      if (t.get('tab') === 'backorder') this.openTab = "BOS";
      if (t.get('tab') === 'shipped') this.openTab = "OSS";
      if (t.get('tab') === 'paid') this.openTab = "MSH";
    });

    this.loadTabData();
  }

  canAccessPermission(requiredPermission: number): boolean {
    const user = this.authService.user;
    const userType = Number(user?.userType);
    const userPermissions = user?.permissions || 0;
    const allowUserType4 = PermissionMetadata[requiredPermission]?.allowUserType4 ?? false;
    return ((userPermissions & requiredPermission) > 0) || (allowUserType4 && userType === 4);
  }

  onLazyLoad(event: any) {
    const start = event.first;
    const end = start + event.rows;
    const tab = this.openTab;
    if (this.allData[tab]) {
      this.visibleData[tab] = this.allData[tab].slice(start, end);
    }
  }


  clearFilters(dt: Table) {
    dt.clear(); // clear all filters
    if (this.globalFilter?.nativeElement) {
      this.globalFilter.nativeElement.value = ''; // reset input
    }
  }
  sanitizeHtml(value: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value);
  }
  get frozenRows(): any[] {
    return this.visibleData[this.openTab]?.map(r => ({ po: r.po })) || [];
  }

  get currentTabData() {
    return this.visibleData?.[this.openTab] || [];
  }



  getTotal(field: string): number {
    return this.currentTabData.reduce((sum, item) => {
      const val = parseFloat(item[field]);
      return sum + (isNaN(val) ? 0 : val);
    }, 0) ?? 0;
  }



  loadTabData() {
    this.loading = true;
    if (!this.allData[this.openTab]) {
      let apiCall: Observable<any>;
      switch (this.openTab) {
        case 'MSS': apiCall = this.dataService.getStatementSummary(this.searchstring || null); break;
        case 'BOS': apiCall = this.dataService.getBackordersSummary(); break;
        case 'MSH': apiCall = this.dataService.getStatementHistory(); break;
        case 'OSS': apiCall = this.dataService.getShippedSummary().pipe(map((i: any) => i.map(item => new ShippedSummary(item)))); break;
        default: apiCall = new Observable<any[]>(); break;
      }
      apiCall.subscribe((r: any[]) => {
        this.allData[this.openTab] = r;
        this.totalRecords[this.openTab] = r.length;
        this.visibleData[this.openTab] = r.slice(0, 100);
        this.tableInfo = this.allTableInfo[this.openTab];
        this.loading = false;
      });
    } else {
      this.visibleData[this.openTab] = this.allData[this.openTab].slice(0, 100);
      this.tableInfo = this.allTableInfo[this.openTab];
      this.loading = false;
    }
  }

  setTab(tab: string) {
    this.pageNumber = 1;
    this.openTab = tab;
    this.visibleData[tab] = [];
    this.loadTabData();
  }
  onExportTab(tab: string) {
    this.exportToExcel(tab);
  }
  exportToExcel(tabName: string) {
    const data = this.visibleData[tabName];
    const columns = this.tableInfo?.columns;
    const sheetName = this.tabNameMap[tabName] || tabName;

    if (!data || data.length === 0 || !columns) return;

    const formatted = data.map(row => {
      const formattedRow: any = {};
      for (const col of columns) {
        const rawValue = row[col.field];
        let value = rawValue;

        if (col.type === 'date') {
          value = rawValue
            ? new Date(rawValue).toLocaleDateString('en-US')
            : '';
        } else if (col.type === 'currency') {
          value = typeof rawValue === 'number'
            ? `$${rawValue.toFixed(2)}`
            : rawValue;
        }

        formattedRow[col.title] = value;
      }
      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formatted);

    const maxWidths = columns.map(col =>
      Math.max(
        col.title.length,
        ...formatted.map(row => (row[col.title] ? row[col.title].toString().length : 0))
      )
    );
    worksheet['!cols'] = maxWidths.map(w => ({ wch: w + 2 }));

    const workbook: XLSX.WorkBook = {
      Sheets: { [sheetName]: worksheet },
      SheetNames: [sheetName],
    };

    const filename = `${sheetName}_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }



  exportAllTabsToExcel() {
    const workbook: XLSX.WorkBook = { Sheets: {}, SheetNames: [] };

    Object.keys(this.allData).forEach(tab => {
      const data = this.allData[tab];
      const columns = this.allTableInfo[tab]?.columns;
      const sheetName = this.tabNameMap[tab] || tab;

      if (!data || data.length === 0 || !columns) return;

      const formatted = data.map(row => {
        const formattedRow: any = {};
        for (const col of columns) {
          const rawValue = row[col.field];
          let value = rawValue;

          if (col.type === 'date') {
            value = rawValue
              ? new Date(rawValue).toLocaleDateString('en-US')
              : '';
          } else if (col.type === 'currency') {
            value = typeof rawValue === 'number'
              ? `$${rawValue.toFixed(2)}`
              : rawValue;
          }

          formattedRow[col.title] = value;
        }
        return formattedRow;
      });

      const worksheet = XLSX.utils.json_to_sheet(formatted);

      const maxWidths = columns.map(col =>
        Math.max(
          col.title.length,
          ...formatted.map(row => (row[col.title] ? row[col.title].toString().length : 0))
        )
      );
      worksheet['!cols'] = maxWidths.map(w => ({ wch: w + 2 }));

      workbook.SheetNames.push(sheetName);
      workbook.Sheets[sheetName] = worksheet;
    });

    const filename = `Full_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }



  get globalFilterFields(): string[] {
    return this.tableInfo?.columns?.map(col => col.field) || [];
  }

  showDownloadOptions() {
    this.store.dispatch(new ShowDownloadDialog(this.visibleData[this.openTab], this.allTableInfo[this.openTab], []));
  }

  downloadPayables() {
    this.dataService.downloadPayables().subscribe((resp: any) => {
      const a = document.createElement('a');
      a.setAttribute('style', 'display:none;');
      document.body.appendChild(a);
      a.download = 'Billings.pdf';
      a.href = URL.createObjectURL(resp);
      a.target = '_blank';
      a.click();
      document.body.removeChild(a);
    }, err => {
      switch (err.error.size) {
        case 30:
          this.store.dispatch(new ErrorMessageSet("The invoice was not found")); break;
        case 32:
          this.store.dispatch(new ErrorMessageSet("The invoice number was not valid")); break;
      }
    });
  }



  downloadInvoice(row, caller) {
    const id = row.invoice;
    caller.dataService.downloadInvoice(id).subscribe((resp: any) => {
      let wdw = window.open('', 'invoice');
      wdw.location.href = URL.createObjectURL(resp);
    }, err => {
      switch (err.error.size) {
        case 30:
          caller.store.dispatch(new ErrorMessageSet("The invoice was not found")); break;
        case 32:
          caller.store.dispatch(new ErrorMessageSet("The invoice number was not valid")); break;
      }
    });
  }

  get allTableInfo() {
    return {
      MSS: new TableInfo({
        columns: [
          { title: 'Date', field: 'datePosted', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Invoice', field: 'invoice', type: 'action', callback: this.downloadInvoice, sort: true },
          { title: 'Tracking No', field: 'tracking', sort: true },
          { title: 'Member PO', field: 'po', sort: true },
          { title: 'Gross', field: 'gross', type: 'currency', sort: true },
          { title: 'Disc', field: 'disc', sort: true },
          { title: 'Due on', field: 'dueOn', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Discount', field: 'discount', type: 'currency', sort: true },
          { title: 'Net', field: 'net', type: 'currency', sort: true },
          { title: 'Vendor Invoice #', field: 'vendorInvoice', sort: true },
          { title: 'Vendor', field: 'vendorName', sort: true },
        ]
      }),
      MSH: new TableInfo({
        columns: [
          { title: 'Date', field: 'datePosted', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Invoice', field: 'invoice', type: 'action', callback: this.downloadInvoice, sort: true },
          { title: 'Member PO', field: 'po', sort: true },
          { title: 'Gross', field: 'gross', type: 'currency', sort: true },
          { title: 'Disc', field: 'disc', sort: true },
          { title: 'Due on', field: 'dueOn', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Date Paid', field: 'datePaid', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Discount', field: 'discount', type: 'currency', sort: true },
          { title: 'Net', field: 'net', type: 'currency', sort: true },
          { title: 'Vendor Invoice #', field: 'vendorInvoice', sort: true },
          { title: 'Vendor', field: 'vendorName', sort: true },
        ]
      }),
      BOS: new TableInfo({
        columns: [
          { title: 'PO', field: 'poNumber', sort: true },
          { title: 'Date', field: 'date', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Product', field: 'productCode', sort: true },
          { title: 'Description', field: 'modelName', sort: true },
          { title: 'Quantity', field: 'leftOver', sort: true },
          { title: 'Est Del', field: 'dueDate', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Weborder', field: 'webOrder', sort: true },
          { title: 'Cost', field: 'cost', type: 'currency', sort: true },
          { title: 'Ext. Cost', field: 'costExt', type: 'currency', sort: true },
        ]
      }),
      OSS: new TableInfo({
        columns: [
          { title: 'PO', field: 'po' },
          { title: 'Ship Date', field: 'shipDate', type: 'date', format: 'MM/dd/yyyy', sort: true },
          { title: 'Product', field: 'product', sort: true },
          { title: 'Description', field: 'description', sort: true },
          { title: 'Quantity', field: 'quantity', sort: true },
          { title: 'Packages', field: 'packages', sort: true },
          { title: 'Tracking', field: 'trackingLinks', sort: true },
        ]
      })
    };
  }
}
